import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CAMPAIGN } from "@/constants/testIds";
import { ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";

const OBJECTIVES = ["AWARENESS", "TRAFFIC", "ENGAGEMENT", "CONVERSION", "LEADS"];
const PLATFORMS = ["meta", "linkedin", "tiktok", "x", "google_youtube"];

export default function CampaignNew() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", objective: "AWARENESS", description: "",
    brand_color: "#ee9037", target_platforms: ["meta"], status: "draft",
  });
  const [busy, setBusy] = useState(false);

  const togglePlatform = (p) => setForm((f) => ({
    ...f,
    target_platforms: f.target_platforms.includes(p)
      ? f.target_platforms.filter((x) => x !== p) : [...f.target_platforms, p],
  }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/campaigns", form);
      toast.success("Campaign created");
      nav(`/campaigns/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create campaign");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-8 max-w-3xl">
      <Link to="/campaigns" className="text-xs text-gray-500 hover:text-gray-900 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="h-3.5 w-3.5" /> All campaigns
      </Link>
      <h1 className="text-3xl font-display text-gray-900">New campaign</h1>
      <p className="text-sm text-gray-500 mt-1">Set up the basics — you can fine-tune later.</p>

      <form onSubmit={save} className="mt-6 bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
        {/* Name */}
        <section className="px-6 py-5">
          <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Name</label>
          <input
            data-testid={CAMPAIGN.nameInput} required value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Codira S1 — Launch"
            className="w-full rounded-lg border border-gray-200 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 px-3.5 py-2.5 text-sm"
            autoFocus />
        </section>

        {/* Objective + brand color */}
        <section className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Objective</label>
            <select data-testid={CAMPAIGN.objectiveSelect} value={form.objective}
              onChange={(e) => setForm({ ...form, objective: e.target.value })}
              className="w-full rounded-lg border border-gray-200 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 px-3.5 py-2.5 text-sm bg-white">
              {OBJECTIVES.map((o) => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Brand color</label>
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20 px-2 py-1">
              <input type="color" data-testid={CAMPAIGN.brandColorInput} value={form.brand_color}
                onChange={(e) => setForm({ ...form, brand_color: e.target.value })}
                className="w-9 h-9 bg-transparent border-0 cursor-pointer" />
              <input value={form.brand_color}
                onChange={(e) => setForm({ ...form, brand_color: e.target.value })}
                className="flex-1 bg-transparent outline-none text-sm font-mono" />
            </div>
          </div>
        </section>

        {/* Description */}
        <section className="px-6 py-5">
          <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Description</label>
          <textarea data-testid={CAMPAIGN.descTextarea} rows={3} value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Brief context for your team — angle, dates, goal."
            className="w-full rounded-lg border border-gray-200 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 px-3.5 py-2.5 text-sm resize-none" />
        </section>

        {/* Platforms */}
        <section className="px-6 py-5">
          <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Target platforms</label>
          <div className="flex flex-wrap gap-2">
            {PLATFORMS.map((p) => {
              const active = form.target_platforms.includes(p);
              return (
                <button type="button" key={p} onClick={() => togglePlatform(p)}
                  className={`px-3.5 py-1.5 text-xs rounded-md border transition-colors ${
                    active ? "border-brand bg-brand text-white" : "border-gray-200 text-gray-600 hover:border-gray-400"
                  }`}>
                  {p.replace("_", " / ")}
                </button>
              );
            })}
          </div>
        </section>

        {/* Footer */}
        <section className="px-6 py-4 bg-gray-50 rounded-b-xl flex items-center justify-end gap-2">
          <Link to="/campaigns"
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-white transition-colors">
            Cancel
          </Link>
          <button data-testid={CAMPAIGN.saveBtn} type="submit" disabled={busy}
            className="btn-brand inline-flex items-center gap-2 px-5 py-2 text-sm disabled:opacity-50">
            <Sparkles className="h-4 w-4" /> {busy ? "Creating…" : "Create campaign"}
          </button>
        </section>
      </form>
    </div>
  );
}
